import {
  flatten,
  groupBy,
  sortBy,
  maxBy,
  minBy,
  uniq,
  uniqBy,
  last,
  findLast,
  take,
  some
} from "lodash";

/**
 * DON'T EXTEND THIS FURTHER
 */

type SortPredicate<T> = (x: T) => any;

Array.prototype.all = function (predicate) {
  return this.reduce((p, c) => (predicate(c) ? p : false), true);
};

Array.prototype.hasAny = function (list) {
  return this.any((x) => list.includes(x));
};

Array.prototype.max = function (predicate) {
  return maxBy([...this], predicate);
};

Array.prototype.min = function (predicate) {
  return minBy([...this], predicate);
};

Array.prototype.flatMapWith = function (predicate, withFn) {
  const result = [];

  for (const el of this) {
    const values = predicate(el);
    for (const value of values) {
      result.push(withFn(value, el));
    }
  }

  return result;
};

Array.prototype.pushOrUpdate = function (value, where) {
  if (typeof this[0] === "function") {
    throw new Error("no op: can't be an array of functions");
  }

  const getValue = (found) => (typeof value === "function" ? value(found) : value);

  const existingIndex = this.findIndex(where);
  return existingIndex === -1
    ? this.push(getValue(null))
    : (this[existingIndex] = getValue(this[existingIndex]));
};

Array.prototype.sortBy = function (...params: Array<SortPredicate<any>>) {
  return sortBy([...this], params);
};

Array.prototype.selectMany = function (predicate) {
  return this.reduce((a, b) => a.concat(predicate(b)), []);
};

Array.prototype.toLabelValue = function (mapLabel, mapValue) {
  return this.map((v, i) => ({
    label: mapLabel ? mapLabel(v, i) : v.toString(),
    value: mapValue ? mapValue(v, i) : v.toString()
  }));
};

Array.prototype.toRecord = function (mapKey, mapValue) {
  return this.reduce((p, c) => {
    p[mapKey(c)] = mapValue(c);
    return p;
  }, {});
};

Array.prototype.unique = function () {
  return uniq([...this]);
};
Array.prototype.uniqueBy = function (predicate = (x) => x) {
  return uniqBy([...this], predicate);
};

Array.prototype.last = function (predicate) {
  return predicate ? findLast([...this], predicate) : last([...this]);
};

Array.prototype.groupBy = function (predicate) {
  const items = groupBy([...this], predicate);
  return Object.keys(items).map((key) => ({ key, values: items[key] }));
};
Array.prototype.take = function (count: number) {
  return take([...this], count);
};
Array.prototype.skip = function (count: number) {
  return this.slice(count, this.length);
};

Array.prototype.any = function (predicate) {
  return some([...this], predicate);
};

Array.prototype.toggle = function (element, uniqueBy) {
  const index = this.findIndex((x) =>
    uniqueBy ? uniqueBy(x) === uniqueBy(element) : x === element
  );
  if (index > -1) {
    this.splice(index, 1);
  } else {
    this.push(element);
  }
};

Array.prototype.toggleI = function (element, uniqueBy) {
  const index = this.findIndex((x) =>
    uniqueBy ? uniqueBy(x) === uniqueBy(element) : x === element
  );

  if (index > -1) {
    return this.filter((x) => x !== element);
    this.splice(index, 1);
  } else {
    return [...this, element];
  }
};

/**
 * removes the first matching element from array
 * @warn it doesn't work with mobx observables, because mobx provides
 *       its own implementation of "remove"
 *       use removeElement in this case
 */
Array.prototype.remove = function (elementOrCallback) {
  const predicate =
    typeof elementOrCallback === "function"
      ? elementOrCallback
      : (x) => x === elementOrCallback;

  const index = this.findIndex(predicate);

  if (index === -1) {
    return false;
  }

  this.splice(index, 1);
  return true;
};

Array.prototype.removeMany = function (elementOrCallback) {
  let result = true;
  let removed = 0;

  while (result) {
    result = this.remove(elementOrCallback);
    if (result) {
      removed++;
    }
  }

  return removed;
};

Array.prototype.removeElement = Array.prototype.remove;

Array.prototype.removeElementPure = function (predicate) {
  const index = this.findIndex(predicate);

  if (index === -1) {
    return [...this];
  }

  return this.filter((x, i) => i !== index);
};

if (typeof Array.prototype.flat === "undefined") {
  Array.prototype.flat = function () {
    return flatten(this as any);
  };
}
